import { useMemo } from 'react';

import type { BehaviorTrackingProps } from '../types/behavior-tracking-props.js';
import { behaviorTrackingPropKeys } from '../utils/extract-behavior-tracking-props.js';

/**
 * Utility function to extract behavioral tracking props from component props to easily spread them to the desired child.
 * @internal
 */
export const useBehavioralTrackingProps = <TProps extends object>(
  props: TProps,
): [BehaviorTrackingProps, Omit<TProps, keyof BehaviorTrackingProps>] =>
  useMemo(() => {
    const behavioralTrackingProps: BehaviorTrackingProps = {};
    const restProps: Omit<TProps, keyof BehaviorTrackingProps> = { ...props };

    for (const [key, value] of Object.entries(props)) {
      if (
        behaviorTrackingPropKeys.includes(key as keyof BehaviorTrackingProps) &&
        typeof value === 'string'
      ) {
        behavioralTrackingProps[key as keyof BehaviorTrackingProps] = value;
        delete restProps[key as keyof typeof restProps];
      }
    }

    return [behavioralTrackingProps, restProps];
  }, [props]);
